// Immutable edits on a project document. Every function returns a new project.

import { Matrix4, Quaternion, Euler, Vector3, rad, deg, rotFor } from './geometry.js'
import { HW_KINDS } from './materials.js'

export const newId = () => `p${Math.random().toString(36).slice(2, 8)}`

export const updatePart = (P, id, patch) => ({ ...P, parts: P.parts.map((p) => (p.id === id ? { ...p, ...(typeof patch === 'function' ? patch(p) : patch) } : p)) })

export const addPart = (P, part) => ({ ...P, parts: [...P.parts, part] })

export function removePart(P, id) {
  const joints = Object.fromEntries(Object.entries(P.joints || {}).filter(([k]) => !k.split('|').some((s) => s.split(':')[0] === id)))
  return { ...P, parts: P.parts.filter((p) => p.id !== id), joints }
}

export function duplicatePart(P, id, offset) {
  const src = P.parts.find((p) => p.id === id)
  if (!src) return { project: P, id: null }
  const nid = newId()
  const copy = { ...structuredClone(src), id: nid, name: nextName(P, src.name), pos: src.pos.map((v, i) => v + (offset?.[i] || 0)) }
  return { project: addPart(P, copy), id: nid }
}

/** Mirror copy across the plane x = cx (left side → right side). */
export function mirrorPart(P, id, cx = 0) {
  const src = P.parts.find((p) => p.id === id)
  if (!src) return { project: P, id: null }
  const M = new Matrix4().makeScale(-1, 1, 1)
  const R = new Matrix4().makeRotationFromEuler(new Euler(rad(src.rot[0]), rad(src.rot[1]), rad(src.rot[2]), 'XYZ'))
  // M R M is a proper rotation; with a flipped width axis the part keeps its handedness.
  const R2 = M.clone().multiply(R).multiply(M)
  const e = new Euler().setFromQuaternion(new Quaternion().setFromRotationMatrix(R2), 'XYZ')
  const r = (x) => Math.round(deg(x) * 100) / 100
  const nid = newId()
  const name = /links/.test(src.name) ? src.name.replace('links', 'rechts') : /rechts/.test(src.name) ? src.name.replace('rechts', 'links') : nextName(P, src.name)
  const copy = { ...structuredClone(src), id: nid, name, pos: [2 * cx - src.pos[0], src.pos[1], src.pos[2]], rot: [r(e.x), r(e.y), r(e.z)] }
  return { project: addPart(P, copy), id: nid }
}

export function nextName(P, base) {
  const stem = String(base || 'Teil').replace(/\s\d+$/, '')
  const used = new Set(P.parts.map((p) => p.name))
  for (let i = 2; i < 999; i++) if (!used.has(`${stem} ${i}`)) return `${stem} ${i}`
  return stem
}

export const setJoint = (P, key, patch) => ({ ...P, joints: { ...(P.joints || {}), [key]: { ...(P.joints?.[key] || {}), ...patch } } })
export function setJointParam(P, key, k, v) {
  const cur = P.joints?.[key] || {}
  return { ...P, joints: { ...(P.joints || {}), [key]: { ...cur, params: { ...(cur.params || {}), [k]: v } } } }
}
export const setDefault = (P, kind, type) => ({ ...P, defaults: { ...(P.defaults || {}), [kind]: type } })

/** Clears per-joint choices of one contact kind so the new default applies everywhere. */
export function resetJointsOfKind(P, derived, kind) {
  const keys = new Set(derived.joints.filter((j) => j.kind === kind).map((j) => j.key))
  return { ...P, joints: Object.fromEntries(Object.entries(P.joints || {}).filter(([k]) => !keys.has(k))) }
}

export const ORIENTATIONS = [
  { id: 'lx', label: 'liegt · X', rot: rotFor('+x', '+z') },
  { id: 'lz', label: 'liegt · Z', rot: rotFor('+z', '-x') },
  { id: 'sv', label: 'steht · vorn', rot: rotFor('+y', '+x') },
  { id: 'ss', label: 'steht · seitl.', rot: rotFor('+y', '+z') },
  { id: 'hx', label: 'hochkant · X', rot: rotFor('+x', '+y') },
  { id: 'hz', label: 'hochkant · Z', rot: rotFor('+z', '+y') },
]

export const ADDABLE = [
  { id: 'brett', label: 'Brett', icon: 'rectangle', make: (m) => ({ type: 'board', name: 'Brett', material: m, L: 600, W: 200, T: 18, rot: rotFor('+x', '+z') }) },
  { id: 'platte', label: 'Platte', icon: 'crop_landscape', make: (m) => ({ type: 'board', name: 'Platte', material: m, L: 800, W: 400, T: 18, rot: rotFor('+x', '+z') }) },
  { id: 'leiste', label: 'Leiste', icon: 'remove', make: (m) => ({ type: 'board', name: 'Leiste', material: m, L: 600, W: 40, T: 20, rot: rotFor('+x', '+y') }) },
  { id: 'kantholz', label: 'Kantholz', icon: 'view_column', make: (m) => ({ type: 'board', name: 'Kantholz', material: m, L: 600, W: 45, T: 45, rot: rotFor('+y', '+x') }) },
  { id: 'rundstab', label: 'Rundstab', icon: 'horizontal_rule', make: (m) => ({ type: 'rod', name: 'Rundstab', material: m, L: 500, D: 20, rot: rotFor('+x', '+y') }) },
  { id: 'scheibe', label: 'Scheibe', icon: 'circle', make: (m) => ({ type: 'rod', name: 'Scheibe', material: m, L: 18, D: 300, rot: rotFor('+y', '+x') }) },
  { id: 'fassung', label: 'Fassung', icon: 'lightbulb_circle', hw: true, make: () => ({ type: 'hw', hw: 'fassung', name: HW_KINDS.fassung.name, L: 62, D: 40, rot: rotFor('+y', '+x') }) },
  { id: 'birne', label: 'Leuchtmittel', icon: 'lightbulb', hw: true, make: () => ({ type: 'hw', hw: 'birne', name: HW_KINDS.birne.name, D: 95, rot: rotFor('-y', '+x') }) },
  { id: 'kabel', label: 'Kabel', icon: 'cable', hw: true, make: () => ({ type: 'hw', hw: 'kabel', name: HW_KINDS.kabel.name, L: 800, D: 6, rot: rotFor('+y', '+x') }) },
  { id: 'diffusor', label: 'Diffusor', icon: 'blur_circular', hw: true, make: () => ({ type: 'hw', hw: 'diffusor', name: HW_KINDS.diffusor.name, L: 3, D: 420, rot: rotFor('+y', '+x') }) },
  { id: 'led', label: 'LED-Modul', icon: 'light_mode', hw: true, make: () => ({ type: 'hw', hw: 'led', name: HW_KINDS.led.name, L: 12, D: 300, rot: rotFor('+y', '+x') }) },
  { id: 'ledstrip', label: 'LED-Streifen', icon: 'linear_scale', hw: true, make: () => ({ type: 'hw', hw: 'ledstrip', name: HW_KINDS.ledstrip.name, L: 300, W: 10, T: 2, rot: rotFor('+x', '+y') }) },
  { id: 'diffusorplatte', label: 'Diffusor-Platte', icon: 'crop_din', hw: true, make: () => ({ type: 'hw', hw: 'platte', name: HW_KINDS.platte.name, L: 300, W: 150, T: 2, rot: rotFor('+x', '+y') }) },
  { id: 'baldachin', label: 'Baldachin', icon: 'radio_button_checked', hw: true, make: () => ({ type: 'hw', hw: 'baldachin', name: HW_KINDS.baldachin.name, L: 25, D: 100, rot: rotFor('+y', '+x') }) },
  { id: 'kaufteil', label: 'Kaufteil', icon: 'shopping_bag', hw: true, make: () => ({ type: 'hw', hw: 'box', name: 'Kaufteil', L: 60, W: 40, T: 20, rot: rotFor('+x', '+z') }) },
]

/** Where a new part goes: beside the selection, else on top of everything. */
export function placeNew(part, { box, selected }) {
  const ext = part.type === 'rod' || part.hw ? [part.L || part.D, part.D, part.D] : [part.L, part.W, part.T]
  const q = new Quaternion().setFromEuler(new Euler(rad(part.rot[0]), rad(part.rot[1]), rad(part.rot[2]), 'XYZ'))
  const half = new Vector3()
  for (let i = 0; i < 3; i++) {
    const a = new Vector3().setComponent(i, ext[i] / 2).applyQuaternion(q)
    half.x += Math.abs(a.x); half.y += Math.abs(a.y); half.z += Math.abs(a.z)
  }
  if (selected) {
    const sp = selected.shape.box
    return [Math.round(sp.max.x + 60 + half.x), Math.round(sp.min.y + half.y), Math.round((sp.min.z + sp.max.z) / 2)]
  }
  if (!box || !Number.isFinite(box.max.y)) return [0, Math.round(half.y), 0]
  return [Math.round((box.min.x + box.max.x) / 2), Math.round(box.max.y + 80 + half.y), Math.round((box.min.z + box.max.z) / 2)]
}

/** Puts a part down on the floor (lowest corner at y = 0). */
export function dropToFloor(P, derived, id) {
  const d = derived.parts.get(id)
  if (!d) return P
  const minY = Math.min(...d.shape.cornersW.map((p) => p.y))
  return updatePart(P, id, (p) => ({ pos: [p.pos[0], Math.round((p.pos[1] - minY) * 100) / 100, p.pos[2]] }))
}

/** Summary for the project list. */
export function summarize(P) {
  const parts = P.parts || []
  const mats = {}
  for (const p of parts) if (p.type !== 'hw') mats[p.material] = (mats[p.material] || 0) + 1
  return { parts: parts.filter((p) => p.type !== 'hw').length, material: Object.entries(mats).sort((a, b) => b[1] - a[1])[0]?.[0] || null, template: P.template || null }
}
